import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useApp } from "../context/AppContext";
import {
  assignments as assignmentsApi, users as usersApi, files as filesApi,
} from "../lib/api";
import { localizedTemplate, localizedCategory } from "../lib/i18nContent";
import { STATUS_META, PRIORITY_META, fmtDate, fmtDuration } from "../lib/assignmentMeta";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, ClipboardCheck, Store as StoreIcon, ListTodo, Play, CheckCircle2,
  RefreshCw, XCircle, Trash2, Paperclip, Send, Loader2, Camera, History,
  MessageSquareText, User, Calendar, Clock, AlertTriangle, Check, AtSign, FileText,
} from "lucide-react";

const ITEM_STATUSES = [
  { key: "complete", i18n: "assignments.item_complete", cls: "bg-emerald-600 text-white", idle: "text-emerald-700 border-emerald-200" },
  { key: "incomplete", i18n: "assignments.item_incomplete", cls: "bg-red-600 text-white", idle: "text-red-700 border-red-200" },
  { key: "na", i18n: "assignments.item_na", cls: "bg-stone-500 text-white", idle: "text-stone-600 border-stone-200" },
];

const HISTORY_ICON = {
  assigned: ClipboardCheck, reassigned: RefreshCw, started: Play,
  completed: CheckCircle2, cancelled: XCircle, due_changed: Calendar,
};

function StatusPill({ status }) {
  const { t } = useTranslation();
  const m = STATUS_META[status] || STATUS_META.pending;
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${m.cls}`}>{t(m.key)}</span>;
}

export default function AssignmentDetailPage() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, can, setActiveAuditId, setActiveStore } = useApp();
  const isManager = can("manage_assignments");

  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [commentText, setCommentText] = useState("");
  const [commentPhoto, setCommentPhoto] = useState(null);
  const [mentions, setMentions] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [sending, setSending] = useState(false);

  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignTo, setReassignTo] = useState("");
  const [reassignNote, setReassignNote] = useState("");

  const load = useCallback(async () => {
    try {
      const data = await assignmentsApi.get(id);
      setA(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("assignments.toast_error"));
      navigate("/assignments");
    } finally {
      setLoading(false);
    }
  }, [id, navigate, t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (isManager) usersApi.assignable().then((u) => setParticipants((u || []).filter((x) => x.role === "auditor" || x.role === "gerente"))).catch(() => {});
  }, [isManager]);

  if (loading) return <div className="p-16 text-center text-stone-400">{t("common.loading")}</div>;
  if (!a) return null;

  const isOwner = a.auditor_id === user?.id;
  const isTask = a.type === "task_checklist";
  const st = a.effective_status;
  const canStart = (isOwner || isManager) && (st === "pending" || st === "overdue");
  const canContinue = (isOwner || isManager) && st === "in_progress";
  const canComplete = (isOwner || isManager) && st === "in_progress";
  const heading = isTask ? (a.title || t("assignments.type_task_checklist")) : a.store_name;

  const items = a.checklist_items || [];
  const resolved = items.filter((it) => (it.status || "pending") !== "pending").length;
  const pct = items.length ? Math.round((resolved / items.length) * 100) : 0;

  const refresh = () => load();

  const doStart = async () => {
    setBusy(true);
    try {
      const res = await assignmentsApi.start(a.id);
      toast.success(t("assignments.toast_started"));
      if (!isTask && res.audit_id) {
        setActiveAuditId(res.audit_id);
        setActiveStore({ id: a.store_id, tienda: a.store_name, ciudad: a.ciudad });
        navigate(`/audit/${res.audit_id}`);
      } else {
        refresh();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("assignments.toast_error"));
    } finally { setBusy(false); }
  };

  const openAudit = () => {
    if (!a.audit_id) return doStart();
    setActiveAuditId(a.audit_id);
    setActiveStore({ id: a.store_id, tienda: a.store_name, ciudad: a.ciudad });
    navigate(`/audit/${a.audit_id}`);
  };

  const doComplete = async () => {
    setBusy(true);
    try {
      await assignmentsApi.complete(a.id);
      toast.success(t("assignments.toast_completed"));
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("assignments.complete_blocked"));
    } finally { setBusy(false); }
  };

  const doCancel = async () => {
    if (!window.confirm(t("assignments.cancel_confirm"))) return;
    try {
      await assignmentsApi.patch(a.id, { status: "cancelled" });
      toast.success(t("assignments.toast_cancelled"));
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || t("assignments.toast_error")); }
  };

  const doDelete = async () => {
    if (!window.confirm(t("assignments.delete_confirm"))) return;
    try {
      await assignmentsApi.remove(a.id);
      toast.success(t("assignments.toast_deleted"));
      navigate("/assignments");
    } catch (e) { toast.error(e?.response?.data?.detail || t("assignments.toast_error")); }
  };

  const patchField = async (patch) => {
    try {
      await assignmentsApi.patch(a.id, patch);
      toast.success(t("assignments.toast_updated"));
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || t("assignments.toast_error")); }
  };

  const doReassign = async () => {
    if (!reassignTo) return;
    try {
      await assignmentsApi.reassign(a.id, reassignTo, reassignNote);
      toast.success(t("assignments.toast_reassigned"));
      setReassignOpen(false);
      setReassignTo(""); setReassignNote("");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || t("assignments.toast_error")); }
  };

  // ---- checklist item helpers ----
  const saveItem = async (itemId, patch) => {
    try {
      const updated = await assignmentsApi.updateItem(a.id, itemId, patch);
      setA(updated);
    } catch (e) { toast.error(e?.response?.data?.detail || t("assignments.toast_error")); }
  };

  const uploadItemPhoto = async (itemId, file) => {
    if (!file) return;
    try {
      const rec = await filesApi.upload(file, "", "assignment");
      await saveItem(itemId, { photo_url: filesApi.url(rec.id) });
      toast.success(t("assignments.toast_item_saved"));
    } catch { toast.error(t("assignments.toast_error")); }
  };

  // ---- comments ----
  const toggleMention = (u) => {
    setMentions((m) => (m.includes(u.id) ? m.filter((x) => x !== u.id) : [...m, u.id]));
    setCommentText((txt) => (txt.includes(`@${u.name}`) ? txt : `${txt}${txt && !txt.endsWith(" ") ? " " : ""}@${u.name} `));
  };

  const onCommentPhoto = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    try {
      const rec = await filesApi.upload(file, "", "assignment");
      setCommentPhoto({ url: filesApi.url(rec.id), name: rec.original_filename });
    } catch { toast.error(t("assignments.toast_error")); }
    finally { e.target.value = ""; }
  };

  const sendComment = async () => {
    if (!commentText.trim() && !commentPhoto) return;
    setSending(true);
    try {
      await assignmentsApi.addComment(a.id, {
        text: commentText.trim(),
        mentions,
        photo_url: commentPhoto?.url || "",
      });
      setCommentText(""); setCommentPhoto(null); setMentions([]);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || t("assignments.toast_error")); }
    finally { setSending(false); }
  };

  const priorityMeta = PRIORITY_META[a.priority] || PRIORITY_META.medium;

  return (
    <div className="p-6 lg:p-10 max-w-[1200px] mx-auto" data-testid="assignment-detail-page">
      <button onClick={() => navigate("/assignments")} className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-800" data-testid="assignment-back">
        <ArrowLeft size={15} /> {t("assignments.back")}
      </button>

      {/* Header */}
      <div className="mt-4 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <span className={`w-12 h-12 rounded-xl grid place-items-center text-white ${isTask ? "bg-indigo-600" : "bg-[#1E3A8A]"}`}>
            {isTask ? <ListTodo size={22} /> : <StoreIcon size={22} />}
          </span>
          <div>
            <div className="flex items-center gap-2">
              <StatusPill status={st} />
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${priorityMeta.cls}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${priorityMeta.dot}`} /> {t(priorityMeta.key)}
              </span>
            </div>
            <h1 className="font-display text-2xl md:text-3xl mt-1.5">{heading}</h1>
            <p className="text-sm text-stone-500">
              {isTask ? t("assignments.type_task_checklist") : `${a.ciudad || ""}${a.distrito ? ` · ${a.distrito}` : ""}`}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          {canStart && (
            <Button className="brand-button gap-1.5" disabled={busy} onClick={doStart} data-testid="detail-start-button">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />} {t("assignments.start_task")}
            </Button>
          )}
          {!isTask && canContinue && (
            <Button className="brand-button gap-1.5" onClick={openAudit} data-testid="detail-open-audit">
              <ClipboardCheck size={14} /> {t("assignments.continue_audit")}
            </Button>
          )}
          {canComplete && (
            <Button variant="outline" className="gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50" disabled={busy} onClick={doComplete} data-testid="detail-complete-button">
              <CheckCircle2 size={14} /> {t("assignments.complete_task")}
            </Button>
          )}
          {isManager && (
            <>
              <Button variant="outline" className="gap-1.5" onClick={() => setReassignOpen(true)} data-testid="detail-reassign-button">
                <RefreshCw size={14} /> {t("assignments.reassign")}
              </Button>
              {st !== "cancelled" && st !== "completed" && (
                <Button variant="outline" className="gap-1.5 text-stone-600" onClick={doCancel} data-testid="detail-cancel-button">
                  <XCircle size={14} /> {t("assignments.cancel_task")}
                </Button>
              )}
              <Button variant="ghost" className="gap-1.5 text-red-600 hover:bg-red-50" onClick={doDelete} data-testid="detail-delete-button">
                <Trash2 size={14} />
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Meta */}
          <Card className="canales-card p-5" data-testid="assignment-meta">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
              <Meta icon={User} label={t("assignments.auditor")} value={a.auditor_name} />
              <Meta icon={Calendar} label={t("assignments.assigned")} value={fmtDate(a.assigned_date, i18n.language) || "—"} />
              <Meta icon={AlertTriangle} label={t("assignments.due")} value={a.due_date ? fmtDate(a.due_date, i18n.language) : t("assignments.no_due")} />
              <Meta icon={User} label={t("assignments.created_by")} value={a.created_by_name || "—"} />
              {a.result_score != null && <Meta icon={CheckCircle2} label={t("assignments.result_score")} value={`${a.result_score}%`} />}
              {a.time_spent_seconds != null && <Meta icon={Clock} label={t("assignments.time_spent")} value={fmtDuration(a.time_spent_seconds, t)} />}
              {a.completed_by_name && <Meta icon={User} label={t("assignments.completed_by")} value={a.completed_by_name} />}
            </div>

            {!isTask && (a.categories || []).length > 0 && (
              <div className="mt-4 pt-4 border-t border-stone-100">
                <div className="canales-overline mb-2">{t("assignments.categories")}</div>
                <div className="flex flex-wrap gap-1.5">
                  {a.categories.map((c) => (
                    <span key={c} className="inline-flex items-center text-[11px] text-[#1E3A8A] bg-[#E5EBFA] px-2.5 py-1 rounded-full font-medium">
                      {localizedTemplate(c, i18n.language)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {isManager && st !== "completed" && st !== "cancelled" && (
              <div className="mt-4 pt-4 border-t border-stone-100 flex flex-wrap items-end gap-4">
                <div>
                  <div className="canales-overline mb-1">{t("assignments.priority")}</div>
                  <Select value={a.priority} onValueChange={(v) => patchField({ priority: v })}>
                    <SelectTrigger className="h-9 w-32" data-testid="detail-priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["low", "medium", "high"].map((p) => <SelectItem key={p} value={p}>{t(`assignments.priority_${p}`)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="canales-overline mb-1">{t("assignments.due_date")}</div>
                  <Input type="date" defaultValue={a.due_date || ""} onBlur={(e) => { if (e.target.value !== (a.due_date || "")) patchField({ due_date: e.target.value }); }} className="h-9 w-40" data-testid="detail-due-input" />
                </div>
              </div>
            )}
          </Card>

          {/* Instructions & attachments */}
          <Card className="canales-card p-5">
            <div className="canales-overline mb-2">{t("assignments.instructions_from")}</div>
            <p className="text-sm text-stone-700 whitespace-pre-wrap">{a.instructions || t("assignments.no_instructions")}</p>
            {a.comment && <p className="text-sm text-stone-500 mt-2 whitespace-pre-wrap">{a.comment}</p>}
            <div className="mt-4">
              <div className="canales-overline mb-2">{t("assignments.attachments")}</div>
              {(a.attachments || []).length === 0 ? (
                <p className="text-xs text-stone-400">{t("assignments.no_attachments")}</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {a.attachments.map((att, idx) => (
                    (att.content_type || "").startsWith("image/") ? (
                      <a key={idx} href={att.url} target="_blank" rel="noreferrer" className="block w-20 h-20 rounded-lg overflow-hidden border border-stone-200">
                        <img src={att.url} alt={att.name} className="w-full h-full object-cover" />
                      </a>
                    ) : (
                      <a key={idx} href={att.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-stone-200 text-xs hover:border-stone-300">
                        <FileText size={13} /> {att.name?.slice(0, 24)}
                      </a>
                    )
                  ))}
                </div>
              )}
            </div>
          </Card>

          {/* Task checklist */}
          {isTask && (
            <Card className="canales-card p-5" data-testid="assignment-checklist">
              <div className="flex items-center justify-between mb-1">
                <div className="canales-overline">{t("assignments.checklist_title")}</div>
                <span className="text-xs font-semibold text-stone-600">{resolved}/{items.length} · {pct}%</span>
              </div>
              <div className="w-full h-1.5 rounded-full bg-stone-100 overflow-hidden mb-4">
                <div className="h-full bg-[#1E3A8A] transition-all" style={{ width: `${pct}%` }} />
              </div>
              <div className="space-y-3">
                {items.map((it) => (
                  <div key={it.id} className="rounded-xl border border-stone-200 p-3" data-testid={`checklist-item-${it.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm font-medium flex-1">{it.text}</p>
                      <div className="flex gap-1 shrink-0">
                        {ITEM_STATUSES.map((s) => {
                          const active = (it.status || "pending") === s.key;
                          const disabled = !(isOwner || isManager) || st === "completed" || st === "cancelled";
                          return (
                            <button
                              key={s.key}
                              disabled={disabled}
                              onClick={() => saveItem(it.id, { status: active ? "pending" : s.key })}
                              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-all disabled:opacity-40 ${active ? s.cls : `bg-white ${s.idle}`}`}
                              data-testid={`item-status-${it.id}-${s.key}`}
                            >
                              {t(s.i18n)}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    {(isOwner || isManager) && st !== "completed" && st !== "cancelled" && (
                      <div className="mt-2 flex items-center gap-2">
                        <Input
                          defaultValue={it.comment || ""}
                          placeholder={t("assignments.item_comment_ph")}
                          className="h-8 text-xs"
                          onBlur={(e) => { if (e.target.value !== (it.comment || "")) saveItem(it.id, { comment: e.target.value }); }}
                          data-testid={`item-comment-${it.id}`}
                        />
                        <label className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-stone-200 text-[11px] cursor-pointer hover:border-stone-300 shrink-0">
                          <Camera size={12} /> {it.photo_url ? t("assignments.add_another_photo") : t("assignments.add_photo")}
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadItemPhoto(it.id, (e.target.files || [])[0])} data-testid={`item-photo-${it.id}`} />
                        </label>
                      </div>
                    )}
                    {it.photo_url && (
                      <a href={it.photo_url} target="_blank" rel="noreferrer" className="inline-block mt-2 w-16 h-16 rounded-lg overflow-hidden border border-stone-200">
                        <img src={it.photo_url} alt="" className="w-full h-full object-cover" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Store audit corrective actions */}
          {!isTask && (a.audit_findings || []).length > 0 && (
            <Card className="canales-card p-5">
              <div className="canales-overline mb-3">{t("nav.findings")}</div>
              <div className="space-y-2">
                {a.audit_findings.map((f) => (
                  <div key={f.id} className="flex items-center justify-between gap-3 rounded-lg border border-stone-200 p-2.5">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{f.description}</div>
                      <div className="text-[11px] text-stone-500">{localizedCategory(f.category, i18n.language)}</div>
                    </div>
                    <span className="text-[11px] font-semibold text-stone-600 shrink-0">{f.status}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>

        {/* Right column: comments + history */}
        <div className="space-y-6">
          {/* Comments */}
          <Card className="canales-card p-5" data-testid="assignment-comments">
            <div className="canales-overline mb-3 flex items-center gap-1.5"><MessageSquareText size={13} /> {t("assignments.comments_title")}</div>
            <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
              {(a.comments || []).length === 0 && <p className="text-xs text-stone-400">{t("assignments.no_comments")}</p>}
              {(a.comments || []).map((c) => (
                <div key={c.id} className="flex gap-2" data-testid={`comment-${c.id}`}>
                  <span className="w-7 h-7 rounded-full bg-[#1E3A8A] text-[#FACC15] grid place-items-center text-[10px] font-bold shrink-0">
                    {(c.author_name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase()}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 text-[11px] text-stone-500">
                      <span className="font-semibold text-stone-800">{c.author_name}</span>
                      <span className="text-stone-300">·</span>
                      <span>{(c.at || "").slice(0, 16).replace("T", " ")}</span>
                    </div>
                    {c.text && <p className="text-sm text-stone-700 mt-0.5 whitespace-pre-wrap">{c.text}</p>}
                    {c.photo_url && (
                      <a href={c.photo_url} target="_blank" rel="noreferrer" className="inline-block mt-1 w-20 h-20 rounded-lg overflow-hidden border border-stone-200">
                        <img src={c.photo_url} alt="" className="w-full h-full object-cover" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Composer */}
            <div className="mt-3 pt-3 border-t border-stone-100">
              {isManager && participants.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {participants.slice(0, 8).map((u) => (
                    <button
                      key={u.id}
                      onClick={() => toggleMention(u)}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] border ${mentions.includes(u.id) ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "border-stone-200 text-stone-500"}`}
                      data-testid={`mention-${u.id}`}
                    >
                      <AtSign size={10} /> {u.name.split(" ")[0]}
                    </button>
                  ))}
                </div>
              )}
              <Textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder={t("assignments.comment_ph")}
                rows={2}
                className="text-sm"
                data-testid="comment-input"
              />
              {commentPhoto && (
                <div className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] bg-stone-100 px-2 py-1 rounded-full">
                  <Camera size={11} /> {commentPhoto.name?.slice(0, 18)}
                  <button onClick={() => setCommentPhoto(null)} className="text-stone-400 hover:text-red-600"><XCircle size={12} /></button>
                </div>
              )}
              <div className="flex items-center justify-between mt-2">
                <label className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-stone-200 text-[11px] cursor-pointer hover:border-stone-300">
                  <Paperclip size={12} /> {t("assignments.attach_photo")}
                  <input type="file" accept="image/*" className="hidden" onChange={onCommentPhoto} data-testid="comment-photo-input" />
                </label>
                <Button size="sm" className="brand-button gap-1.5" disabled={sending || (!commentText.trim() && !commentPhoto)} onClick={sendComment} data-testid="comment-send">
                  {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} {t("assignments.add_comment")}
                </Button>
              </div>
            </div>
          </Card>

          {/* History */}
          <Card className="canales-card p-5" data-testid="assignment-history">
            <div className="canales-overline mb-3 flex items-center gap-1.5"><History size={13} /> {t("assignments.history_title")}</div>
            <div className="space-y-3">
              {(a.history || []).slice().reverse().map((h, idx) => {
                const Icon = HISTORY_ICON[h.action] || Check;
                return (
                  <div key={idx} className="flex gap-2.5" data-testid={`history-${idx}`}>
                    <span className="w-6 h-6 rounded-full bg-stone-100 grid place-items-center text-stone-500 shrink-0"><Icon size={12} /></span>
                    <div className="min-w-0">
                      <div className="text-sm text-stone-700">
                        {h.action === "reassigned"
                          ? t("assignments.reassigned_from", { from: h.from_auditor || "—", to: h.to_auditor || "—" })
                          : t(`assignments.status_${h.action === "started" ? "in_progress" : h.action === "completed" ? "completed" : h.action === "cancelled" ? "cancelled" : "pending"}`) }
                        {h.action === "assigned" && ` → ${h.to_auditor || a.auditor_name}`}
                        {h.action === "due_changed" && `: ${h.detail}`}
                        {h.action === "completed" && h.detail && ` · ${h.detail}`}
                      </div>
                      <div className="text-[11px] text-stone-400">
                        {h.by_name} · {(h.at || "").slice(0, 16).replace("T", " ")}
                        {h.detail && h.action === "reassigned" && ` · "${h.detail}"`}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
      </div>

      {/* Reassign dialog */}
      <Dialog open={reassignOpen} onOpenChange={setReassignOpen}>
        <DialogContent className="max-w-md" data-testid="reassign-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><RefreshCw size={18} className="text-[#1E3A8A]" /> {t("assignments.reassign_title")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="canales-overline">{t("assignments.reassign_to")}</label>
              <Select value={reassignTo} onValueChange={setReassignTo}>
                <SelectTrigger className="mt-1" data-testid="reassign-auditor-select"><SelectValue placeholder={t("assignments.reassign_to")} /></SelectTrigger>
                <SelectContent>
                  {participants.filter((u) => u.id !== a.auditor_id).map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name} · {t(`roles.${u.role}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="canales-overline">{t("assignments.reassign_note")}</label>
              <Textarea value={reassignNote} onChange={(e) => setReassignNote(e.target.value)} rows={2} className="mt-1" data-testid="reassign-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignOpen(false)}>{t("common.cancel")}</Button>
            <Button className="brand-button gap-1.5" disabled={!reassignTo} onClick={doReassign} data-testid="reassign-confirm">
              <RefreshCw size={14} /> {t("assignments.confirm_reassign")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Meta({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="canales-overline flex items-center gap-1 mb-0.5"><Icon size={11} /> {label}</div>
      <div className="text-sm font-semibold text-stone-800">{value}</div>
    </div>
  );
}
